/**
 * /admin/agency/diners — agency-level drill-down dashboard.
 *
 * Authorized agency admins only. Accepts an X-Agency-Token (stored in
 * localStorage `agency_token`). Lets admins search a diner by email
 * (hashed server-side, never logged) and see aggregate cross-venue risk.
 * Personal contact details are NOT returned by the API — admins drill
 * down per-venue from the restaurant admin page where consent exists.
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const TIER_STYLES = {
  low:      { bg: "#e8f5ee", border: "#b9dec6", color: "#1f7a44", label: "Low risk · welcome" },
  medium:   { bg: "#fff5ed", border: "#f3c4a3", color: "#a64423", label: "Medium · $20 deposit suggested" },
  high:     { bg: "#fde7e7", border: "#f3b3b3", color: "#a64242", label: "High · $35 deposit suggested" },
  critical: { bg: "#1a1408", border: "#1a1408", color: "#f3c84b", label: "Critical · $50 deposit suggested" },
};

export default function AgencyDinersPage() {
  const [token, setToken] = useState(() => localStorage.getItem("agency_token") || "");
  const [tokenInput, setTokenInput] = useState("");
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState("");
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const headers = token ? { "X-Agency-Token": token } : {};

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/api/agency/risk-summary`, { headers })
      .then(({ data }) => setSummary(data))
      .catch((e) => {
        if (e?.response?.status === 401) {
          localStorage.removeItem("agency_token");
          setToken("");
          setError("Token rejected — please re-enter.");
        }
      });
  }, [token]);

  const saveToken = () => {
    if (!tokenInput.trim()) return;
    localStorage.setItem("agency_token", tokenInput.trim());
    setToken(tokenInput.trim());
    setError("");
  };

  const lookup = async (e) => {
    e?.preventDefault();
    setError(""); setProfile(null); setBusy(true);
    try {
      const { data } = await axios.get(
        `${API}/api/agency/diners/lookup?email=${encodeURIComponent(search.trim())}`,
        { headers },
      );
      setProfile(data);
    } catch (e2) {
      setError(e2?.response?.data?.detail || e2.message || "Lookup failed");
    } finally {
      setBusy(false);
    }
  };

  // ── Gate: no token yet ──────────────────────────────────────────
  if (!token) {
    return (
      <div style={{ maxWidth: 480, margin: "80px auto", padding: 32, fontFamily: "Inter, sans-serif" }}>
        <div style={{ fontSize: 11, letterSpacing: 4, textTransform: "uppercase", color: "#a64423", fontWeight: 700, marginBottom: 6 }}>
          Agency drill-down · restricted
        </div>
        <h1 style={{ fontFamily: "Georgia, serif", fontSize: 28, margin: "0 0 12px" }}>Enter agency token</h1>
        <p style={{ color: "#666", fontSize: 13, marginBottom: 20 }}>
          This dashboard aggregates booking history across the SEQ restaurant network.
          PII is never returned by the API — only hashed identifiers + counts.
        </p>
        <input
          value={tokenInput}
          onChange={(e) => setTokenInput(e.target.value)}
          placeholder="X-Agency-Token"
          style={{ width: "100%", padding: 12, borderRadius: 8, border: "1px solid #d6cfc0", fontSize: 14 }}
          data-testid="agency-token-input"
        />
        <button
          onClick={saveToken}
          style={{ marginTop: 10, padding: "12px 20px", background: "#1a1408", color: "#f3c84b", border: "none", borderRadius: 999, fontWeight: 700, letterSpacing: 1.4, textTransform: "uppercase", fontSize: 12, cursor: "pointer" }}
          data-testid="agency-token-submit"
        >
          Continue →
        </button>
        {error && <div style={{ marginTop: 14, color: "#a64242", fontSize: 13 }}>{error}</div>}
      </div>
    );
  }

  const wrapStyle = { maxWidth: 980, margin: "0 auto", padding: "32px 24px 80px", fontFamily: "Inter, sans-serif", color: "#1a1408" };
  return (
    <div style={wrapStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", borderBottom: "1px solid #e6dfd1", paddingBottom: 18, marginBottom: 22 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: 4, textTransform: "uppercase", color: "#a64423", fontWeight: 700, marginBottom: 4 }}>
            🔒 Agency drill-down · hashed PII
          </div>
          <h1 style={{ fontFamily: "Georgia, serif", fontSize: 30, margin: 0, letterSpacing: "-0.4px" }}>Diner risk dashboard</h1>
          <p style={{ color: "#888", fontSize: 13, margin: "4px 0 0" }}>
            Cross-venue aggregate · contact details intentionally excluded — drill down per venue for booking-level data.
          </p>
        </div>
        <button
          onClick={() => { localStorage.removeItem("agency_token"); setToken(""); }}
          style={{ padding: "8px 14px", background: "transparent", border: "1px solid #d6cfc0", borderRadius: 999, fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", color: "#666", cursor: "pointer" }}
          data-testid="agency-logout"
        >
          Sign out
        </button>
      </div>

      {/* ── Network-wide summary ──────────────────────────────────── */}
      {summary && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, marginBottom: 26 }} data-testid="agency-summary">
          <KPI label="Unique diners"     value={summary.unique_diners}    accent="#1a1408" />
          <KPI label="Flagged diners"    value={summary.flagged_diners}   accent="#a64423" />
          <KPI label="Deposit eligible" value={summary.deposit_eligible} accent="#a64242" />
          <KPI label="Critical risk"     value={summary.tier_counts.critical} accent="#1a1408" />
        </div>
      )}

      {/* ── Search ────────────────────────────────────────────────── */}
      <form onSubmit={lookup} style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Diner email · we hash it server-side, never log it"
          type="email"
          required
          style={{ flex: 1, padding: 12, borderRadius: 8, border: "1px solid #d6cfc0", fontSize: 14 }}
          data-testid="agency-search-input"
        />
        <button
          type="submit"
          disabled={busy}
          style={{ padding: "0 22px", background: "#1a1408", color: "#f3c84b", border: "none", borderRadius: 8, fontWeight: 700, letterSpacing: 1.4, textTransform: "uppercase", fontSize: 12, cursor: "pointer" }}
          data-testid="agency-search-submit"
        >
          {busy ? "Looking up…" : "Lookup"}
        </button>
      </form>

      {error && <div style={{ background: "#fff0f0", border: "1px solid #f3c4c4", color: "#a64242", padding: 12, borderRadius: 8, fontSize: 13, marginBottom: 16 }}>{error}</div>}

      {/* ── Result card ──────────────────────────────────────────── */}
      {profile && (
        <DinerProfile profile={profile} />
      )}

      <div style={{ marginTop: 30, background: "#f6f3ea", borderLeft: "4px solid #a64423", padding: "14px 18px", borderRadius: 8, fontSize: 12, color: "#555", lineHeight: 1.55 }}>
        <strong>🔒 Privacy posture:</strong> emails entered here are SHA-256 hashed before any database query.
        Response payloads never include diner name, phone, or address. Agency admins drill down to
        contact details only via the venue admin pages where consent exists.
      </div>
    </div>
  );
}

function KPI({ label, value, accent }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #e6dfd1", borderRadius: 12, padding: 18, boxShadow: "0 6px 14px rgba(20,14,8,0.04)" }}>
      <div style={{ fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", color: "#888", marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: "Georgia, serif", fontSize: 32, color: accent, fontWeight: 700, letterSpacing: "-0.5px" }}>{value}</div>
    </div>
  );
}

function DinerProfile({ profile }) {
  const tier = TIER_STYLES[profile.risk_tier] || TIER_STYLES.low;
  return (
    <div
      data-testid="agency-diner-profile"
      style={{ background: "#fff", border: "1px solid #e6dfd1", borderRadius: 14, padding: 22, marginBottom: 24, boxShadow: "0 8px 22px rgba(20,14,8,0.06)" }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 18, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 11, letterSpacing: 1.4, textTransform: "uppercase", color: "#888" }}>Hashed identity</div>
          <code style={{ fontSize: 11, color: "#666", display: "block", maxWidth: 600, wordBreak: "break-all", fontFamily: "ui-monospace,monospace" }}>
            {profile.email_hash}
          </code>
        </div>
        <div style={{
          padding: "10px 16px", borderRadius: 999, background: tier.bg, color: tier.color, border: `1px solid ${tier.border}`,
          fontSize: 12, fontWeight: 700, letterSpacing: 0.4, whiteSpace: "nowrap",
        }}>
          {tier.label}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 18 }}>
        <Stat label="Total bookings" value={profile.total_bookings} />
        <Stat label="No-shows"       value={profile.total_no_show} highlight={profile.total_no_show > 0 ? "#a64242" : null} />
        <Stat label="Cancelled"      value={profile.total_cancelled} />
        <Stat label="Completed"      value={profile.total_completed} highlight={profile.total_completed > 0 ? "#1f7a44" : null} />
      </div>

      <div style={{ fontSize: 11, letterSpacing: 1.4, textTransform: "uppercase", color: "#888", margin: "8px 0 6px" }}>
        Per venue · {profile.venues_touched} venue{profile.venues_touched === 1 ? "" : "s"}
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>
            <th style={tableH}>Venue</th>
            <th style={tableH}>Total</th>
            <th style={tableH}>No-show</th>
            <th style={tableH}>Cancelled</th>
            <th style={tableH}>Completed</th>
          </tr>
        </thead>
        <tbody>
          {profile.per_venue.map((v) => (
            <tr key={v.venue_slug}>
              <td style={tableD}><strong>{v.venue_slug}</strong></td>
              <td style={tableD}>{v.total}</td>
              <td style={{ ...tableD, color: v.no_show > 0 ? "#a64242" : "#777" }}>{v.no_show}</td>
              <td style={tableD}>{v.cancelled}</td>
              <td style={{ ...tableD, color: v.completed > 0 ? "#1f7a44" : "#777" }}>{v.completed}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {profile.deposit_recommended && (
        <div style={{ marginTop: 14, padding: 14, background: tier.bg, border: `1px solid ${tier.border}`, borderRadius: 10, fontSize: 13, color: tier.color }}>
          💰 <strong>Recommendation:</strong> require ${profile.suggested_deposit_aud} AUD holding deposit on future bookings.
          This is suggested venue-side — the deposit prompt only triggers automatically when the diner re-books at a venue where they have prior incidents.
        </div>
      )}
    </div>
  );
}

const tableH = { textAlign: "left", padding: "10px 12px", background: "#fafaf6", fontSize: 11, letterSpacing: 1.2, textTransform: "uppercase", color: "#888", fontWeight: 600, borderBottom: "1px solid #efe9da" };
const tableD = { padding: "12px", borderBottom: "1px solid #f3eedf" };

function Stat({ label, value, highlight }) {
  return (
    <div>
      <div style={{ fontSize: 10, letterSpacing: 1.4, textTransform: "uppercase", color: "#888", marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: "Georgia, serif", fontSize: 26, color: highlight || "#1a1408", fontWeight: 700 }}>{value}</div>
    </div>
  );
}
